import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperience, IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import {
  CCBouncedErrorData,
  CCMissingEmailErrorData,
  CCNotAssignedErrorData,
  ErroDataType
} from '@dadohr/shared/domainTypes/instance';
import { compact } from 'lodash';
import { getItemDisplayName, resolveExpTitle } from '../../../../helpers/adminName';
import {
  getMessageTemplateById,
  getTaskTemplateById
} from '../../../../../../services/experiences/template';
import type { CCErrorData } from '../../InstanceProblems';

// Mirrors `getIssueTitleText`'s CC_SEND_ERROR branch (ParticipantDialog.tsx)
// exactly, just resolved at collection time instead of render time — see
// the `title` field comment on `CCErrorData`.
const resolveCCTitle = (
  tpl: Doc<IExperienceTemplate>,
  itemType: 'MESSAGE' | 'TASK',
  itemTplId: string
): string => {
  const expTitle = tpl.data.title;
  if (itemType === 'MESSAGE') {
    const msgTpl = getMessageTemplateById(tpl, itemTplId);
    return msgTpl ? resolveExpTitle(getItemDisplayName(msgTpl), expTitle) : '';
  }
  const taskTpl = getTaskTemplateById(tpl, itemTplId);
  return taskTpl ? resolveExpTitle(getItemDisplayName(taskTpl), expTitle) : '';
};

export const collectCCIssues = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>
): CCErrorData[] => {
  return compact(
    d.data.instanceErrors.map((err): CCErrorData | null => {
      if (err.ignore) return null;

      if (err.type === ErroDataType.CANNOT_SEND_CC_MISSING_EMAIL) {
        const e = err as CCMissingEmailErrorData;
        return {
          type: 'CC_SEND_ERROR',
          subtype: 'MISSING_EMAIL',
          recipientType: 'ROLE',
          recipientId: e.actorKey,
          displayName: e.actorName,
          itemType: e.itemType,
          itemTplId: e.itemTplId,
          title: resolveCCTitle(tpl, e.itemType, e.itemTplId),
          tpl,
          instance: d,
          id: e.id,
          at: e.at
        };
      }

      if (err.type === ErroDataType.CANNOT_SEND_CC_NOT_ASSIGNED) {
        const e = err as CCNotAssignedErrorData;
        return {
          type: 'CC_SEND_ERROR',
          subtype: 'NOT_ASSIGNED',
          recipientType: 'ROLE',
          recipientId: e.actorKey,
          displayName: e.roleName,
          itemType: e.itemType,
          itemTplId: e.itemTplId,
          title: resolveCCTitle(tpl, e.itemType, e.itemTplId),
          tpl,
          instance: d,
          id: e.id,
          at: e.at
        };
      }

      if (err.type === ErroDataType.CC_BOUNCED) {
        const e = err as CCBouncedErrorData;
        return {
          type: 'CC_SEND_ERROR',
          subtype: 'BOUNCED',
          recipientType: e.recipientType,
          recipientId: e.recipientId,
          displayName: e.displayName,
          email: e.email,
          itemType: e.itemType,
          itemTplId: e.itemTplId,
          title: resolveCCTitle(tpl, e.itemType, e.itemTplId),
          tpl,
          instance: d,
          id: e.id,
          at: e.at
        };
      }

      return null;
    })
  );
};
